import type {
  QuickInferenceDetail,
  QuickInferenceFrame,
  QuickInferenceSessionListItem,
  QuickInferenceSessionStatus,
  QuickInferenceStatus,
} from '../../../helpers/api/quick-inference/quick-inference';
import type { FrameEntry } from './FrameEditorPanel';

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function truncateFilename(name: string, maxLength = 48): string {
  if (!name || name.length <= maxLength) return name;
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
  const baseMax = Math.max(maxLength - ext.length - 1, 12);
  const base = name.slice(0, name.length - ext.length);
  if (base.length <= baseMax) return name;
  return `${base.slice(0, baseMax)}…${ext}`;
}

export function isDicomFile(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return type.includes('dicom') || name.endsWith('.dcm');
}

export type QuickInferenceFileKind = 'dicom' | 'video' | 'image';

export function fileKind(file: File): QuickInferenceFileKind {
  if (isDicomFile(file)) return 'dicom';
  if ((file.type || '').startsWith('video/')) return 'video';
  return 'image';
}

/** Resolve kind from stored MIME + filename (viewer sidebar / API summaries). */
export function fileKindFromMeta(
  contentType: string | null | undefined,
  filename: string | null | undefined,
  pacsIngestStatus?: 'pending' | 'success' | 'fail' | 'skipped' | null
): QuickInferenceFileKind {
  if (pacsIngestStatus === 'success') {
    return 'dicom';
  }
  const type = (contentType || '').toLowerCase();
  const name = (filename || '').toLowerCase();
  if (type.includes('dicom') || name.endsWith('.dcm') || name.endsWith('.dicom')) {
    return 'dicom';
  }
  if (type.startsWith('video/') || /\.(mp4|webm|mov|avi|mkv)$/.test(name)) {
    return 'video';
  }
  return 'image';
}

export function fileStatusColor(status: QuickInferenceStatus): string {
  switch (status) {
    case 'success':
      return 'green';
    case 'fail':
      return 'red';
    default:
      return 'default';
  }
}

export function sessionStatusColor(status: QuickInferenceSessionStatus): string {
  switch (status) {
    case 'success':
      return 'green';
    case 'fail':
      return 'red';
    case 'partial':
      return 'orange';
    case 'processing':
      return 'blue';
    default:
      return 'default';
  }
}

export function sessionStatusLabel(status: QuickInferenceSessionStatus): string {
  switch (status) {
    case 'success':
      return 'Success';
    case 'fail':
      return 'Failed';
    case 'partial':
      return 'Partial';
    case 'processing':
      return 'Processing';
    case 'pending':
      return 'Pending';
    default:
      return status;
  }
}

export function shortSessionId(id: string): string {
  if (!id) return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function sessionStatusAccent(status: QuickInferenceSessionStatus): string {
  switch (status) {
    case 'success':
      return 'border-l-emerald-500';
    case 'fail':
      return 'border-l-red-500';
    case 'partial':
      return 'border-l-amber-500';
    case 'processing':
      return 'border-l-blue-500';
    default:
      return 'border-l-slate-300';
  }
}

export function sessionDisplayTitle(session: QuickInferenceSessionListItem): string {
  const files = session.files ?? [];
  if (files.length === 1) {
    return truncateFilename(files[0].filename, 40);
  }
  if (files.length > 1) {
    return `${truncateFilename(files[0].filename, 28)} +${files.length - 1}`;
  }
  if (session.file_count === 1) return '1 file';
  return `${session.file_count} files`;
}

export function sessionDateGroup(iso: string | null | undefined): string {
  if (!iso) return 'Older';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Older';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  if (date >= startOfToday) return 'Today';
  if (date >= startOfYesterday) return 'Yesterday';
  if (now.getTime() - date.getTime() < 7 * 86_400_000) return 'This week';
  return 'Older';
}

export function sessionLabel(session: QuickInferenceSessionListItem): string {
  return session.id;
}

export function buildFrameEntries(detail: QuickInferenceDetail | null): FrameEntry[] {
  const frames = detail?.result?.frames;
  if (!frames) return [];
  return Object.entries(frames)
    .map(([index, frame]) => ({
      index,
      frameNumber:
        typeof frame.source_frame_number === 'number'
          ? frame.source_frame_number
          : Number(index),
      frame,
    }))
    .sort((a, b) => a.frameNumber - b.frameNumber);
}

export function findFrameEntryForNumber(
  entries: FrameEntry[],
  frameNumber: number
): FrameEntry | null {
  return (
    entries.find((e) => e.frameNumber === frameNumber) ??
    entries.find((e) => Number(e.index) === frameNumber) ??
    null
  );
}

export function resolveTotalFrameCount(detail: QuickInferenceDetail | null): number {
  if (!detail) return 0;
  if (detail.number_of_frames > 0) return detail.number_of_frames;
  const meta = detail.result?.metadata?.total_frames;
  if (typeof meta === 'number' && meta > 0) return meta;
  const frameEntries = buildFrameEntries(detail);
  if (frameEntries.length > 0) {
    return Math.max(...frameEntries.map((e) => e.frameNumber)) + 1;
  }
  return detail.status === 'success' ? 1 : 0;
}

export function emptyFrameEntry(frameNumber: number): FrameEntry {
  const frame: QuickInferenceFrame = {
    segmentations: [],
    views: [],
    user_views: [],
    pending_user_views: [],
  };
  return {
    index: String(frameNumber),
    frameNumber,
    frame,
  };
}

export function formatViewLabel(view: string): string {
  const raw = String(view ?? '')
    .replace(/^VIEW__/, '')
    .trim();
  if (!raw) return '';

  // Support both snake_case (AXIAL_VIEW) and CamelCase (AxialViewOfFetalBladder).
  const spaced = raw
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  const smallWords = new Set(['of', 'the', 'and', 'a', 'an', 'to', 'in', 'on', 'for', 'with']);
  return spaced
    .split(' ')
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index > 0 && smallWords.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

export function displayedFrameViews(frame: QuickInferenceFrame): string[] {
  const views = frame.views || [];
  const userViews = frame.user_views || [];
  const out: string[] = [];
  for (const code of [...views, ...userViews]) {
    if (!out.includes(code)) out.push(code);
  }
  return out;
}

export function frameHasInferenceOutput(frame: QuickInferenceFrame): boolean {
  return (
    displayedFrameViews(frame).length > 0 ||
    (frame.segmentations?.length ?? 0) > 0 ||
    (frame.pending_user_views?.length ?? 0) > 0
  );
}

/** Prefer first frame with a detected view; else first with structures. */
export function findFirstFrameWithInferenceOutput(entries: FrameEntry[]): number {
  const withView = entries.find((e) => displayedFrameViews(e.frame).length > 0);
  if (withView) return withView.frameNumber;

  const withSeg = entries.find((e) => (e.frame.segmentations?.length ?? 0) > 0);
  if (withSeg) return withSeg.frameNumber;

  return 0;
}

export function frameIndexForNumber(frameNumbers: number[], frameNumber: number): number {
  const idx = frameNumbers.indexOf(frameNumber);
  return idx >= 0 ? idx : 0;
}

const QUAL_FLAG_RE = /^QUAL_FLAG_(VISIBLE|NOT_VISIBLE)_(\d+)$/;

export interface QualityFlagRow {
  viewCode: string;
  categoryId: number;
  /** Matches view-def / View Details: structure expected present vs expected absent. */
  ruleType: 'visible' | 'not_visible';
  /** Raw metric bool — true means the criterion is satisfied. */
  value: boolean;
  passed: boolean;
  /** Alias of ruleType === 'visible' (View Details `expected_visible`). */
  expectedVisible: boolean;
}

export function collectQualityFlagRows(
  frame: QuickInferenceFrame,
  viewCodes: string[]
): QualityFlagRow[] {
  const metrics = frame.quality_metrics;
  if (!metrics) return [];

  const rows: QualityFlagRow[] = [];
  for (const viewCode of viewCodes) {
    const viewMetrics = metrics[viewCode];
    if (!viewMetrics || typeof viewMetrics !== 'object') continue;

    for (const [key, raw] of Object.entries(viewMetrics)) {
      const match = key.match(QUAL_FLAG_RE);
      if (!match || typeof raw !== 'boolean') continue;

      const ruleType = match[1] === 'VISIBLE' ? 'visible' : 'not_visible';
      const categoryId = Number(match[2]);
      if (!Number.isFinite(categoryId)) continue;

      rows.push({
        viewCode,
        categoryId,
        ruleType,
        value: raw,
        passed: raw === true,
        expectedVisible: ruleType === 'visible',
      });
    }
  }

  // View Details / backend: expected_visible first, then not_visible; keep stable within group.
  rows.sort((a, b) => {
    if (a.expectedVisible !== b.expectedVisible) {
      return a.expectedVisible ? -1 : 1;
    }
    return a.categoryId - b.categoryId;
  });

  return rows;
}

/**
 * Whether the criterion implies the structure is currently present on the frame
 * (View Details `isStructureVisibleInTheory`).
 * - visible rule: satisfied → present
 * - not_visible rule: satisfied → absent
 */
export function qualityFlagImpliesStructurePresent(row: Pick<QualityFlagRow, 'ruleType' | 'value'>): boolean {
  if (row.ruleType === 'not_visible') return !row.value;
  return row.value;
}

/** Minimal seg shape for QI save post-processing (matches convertShapesToSegmentations output). */
export interface QiSegmentationLike {
  id?: number | string;
  category_id?: number;
  label?: string | null;
  segment_type?: 'structure' | 'measurement' | string;
  geometry_type?: string;
  type?: string;
  points?: string;
  length?: number;
  perimeter?: number;
  area?: number;
  score?: number;
  is_manual?: boolean;
}

/**
 * QI-only: new biometry category draws emit STRUCTURE + MEASUREMENT rows (same as batch
 * inference). Does not alter General Viewer save behaviour.
 */
export function expandQiBiometrySegmentationsForSave<T extends QiSegmentationLike>(
  shapes: Array<{ isNew?: boolean; categoryId?: number | null }>,
  segmentations: T[]
): T[] {
  return segmentations.flatMap((seg, index) => {
    const shape = shapes[index];
    const label = seg.label ?? '';
    const isNewBiometry =
      Boolean(shape?.isNew) &&
      shape?.categoryId != null &&
      typeof label === 'string' &&
      label.startsWith('MEAS__');

    if (!isNewBiometry) {
      return [seg];
    }

    const { label: measLabel, segment_type: _segmentType, id, ...rest } = seg;
    return [
      {
        ...rest,
        category_id: seg.category_id,
        label: null,
        segment_type: 'structure',
      } as T,
      {
        ...rest,
        category_id: seg.category_id,
        id,
        label: measLabel,
        segment_type: 'measurement',
      } as T,
    ];
  });
}

/** Playground viewer frame filter (file-scoped, client-side). */
export type PlaygroundContentScope = 'all' | 'findings' | 'empty';

export interface PlaygroundViewerFilter {
  contentScope: PlaygroundContentScope;
  views: string[];
  structureIds: number[];
}

export const EMPTY_PLAYGROUND_FILTER: PlaygroundViewerFilter = {
  contentScope: 'all',
  views: [],
  structureIds: [],
};

export function isPlaygroundFilterActive(filter: PlaygroundViewerFilter): boolean {
  return (
    filter.contentScope !== 'all' ||
    filter.views.length > 0 ||
    filter.structureIds.length > 0
  );
}

export function playgroundFilterActiveCount(filter: PlaygroundViewerFilter): number {
  return (
    (filter.contentScope !== 'all' ? 1 : 0) +
    filter.views.length +
    filter.structureIds.length
  );
}

function frameStructureIds(frame: QuickInferenceFrame): number[] {
  const ids = new Set<number>();
  for (const seg of frame.segmentations || []) {
    if (seg.segment_type === 'MEASUREMENT') continue;
    const label = seg.label ?? '';
    if (typeof label === 'string' && label.startsWith('MEAS__')) continue;
    const id = Number(seg.category_id);
    if (Number.isFinite(id)) ids.add(id);
  }
  return Array.from(ids);
}

export interface PlaygroundFilterOption {
  id: string | number;
  label: string;
  count: number;
}

/** Build view/structure options from this file only (better than full-catalog filters). */
export function buildPlaygroundFilterCatalog(
  entries: FrameEntry[],
  allFrameNumbers: number[],
  labelForCategoryId: (id: number) => string,
  viewDisplayName?: (code: string) => string
): { views: PlaygroundFilterOption[]; structures: PlaygroundFilterOption[] } {
  const viewCounts = new Map<string, number>();
  const structureCounts = new Map<number, number>();

  for (const num of allFrameNumbers) {
    const entry = findFrameEntryForNumber(entries, num) ?? emptyFrameEntry(num);
    const views = displayedFrameViews(entry.frame);
    for (const code of views) {
      viewCounts.set(code, (viewCounts.get(code) ?? 0) + 1);
    }
    for (const id of frameStructureIds(entry.frame)) {
      structureCounts.set(id, (structureCounts.get(id) ?? 0) + 1);
    }
  }

  const views = Array.from(viewCounts.entries())
    .map(([code, count]) => ({
      id: code,
      label: viewDisplayName?.(code) || formatViewLabel(code),
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  const structures = Array.from(structureCounts.entries())
    .map(([id, count]) => ({
      id,
      label: labelForCategoryId(id),
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  return { views, structures };
}

export function frameMatchesPlaygroundFilter(
  frame: QuickInferenceFrame,
  filter: PlaygroundViewerFilter
): boolean {
  const views = displayedFrameViews(frame);
  const structureIds = frameStructureIds(frame);
  const hasFindings = views.length > 0 || structureIds.length > 0;

  if (filter.contentScope === 'findings' && !hasFindings) return false;
  if (filter.contentScope === 'empty' && hasFindings) return false;

  if (filter.views.length > 0) {
    const hit = filter.views.some((code) => views.includes(code));
    if (!hit) return false;
  }

  if (filter.structureIds.length > 0) {
    const hit = filter.structureIds.some((id) => structureIds.includes(id));
    if (!hit) return false;
  }

  return true;
}

export function filterFrameNumbers(
  allFrameNumbers: number[],
  entries: FrameEntry[],
  filter: PlaygroundViewerFilter
): number[] {
  if (!isPlaygroundFilterActive(filter)) return allFrameNumbers;
  return allFrameNumbers.filter((num) => {
    const entry = findFrameEntryForNumber(entries, num) ?? emptyFrameEntry(num);
    return frameMatchesPlaygroundFilter(entry.frame, filter);
  });
}
